import { postCommunication, communicationError } from '../../../lib/communicationRequests';
import { SMSMessagePreview } from '../../inbox/SMSMessagePreview';
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Stack,
  Typography,
  Sheet,
  Input,
  Textarea,
  IconButton,
  Chip,
  CircularProgress,
  Button,
} from '@mui/joy';
import {
  PaperPlaneTilt as Send,
  Plus as Add,
  ChatCircle as Comment,
  Check,
  Checks as DoneAll,
  XCircle as ErrorIcon,
  X as Close,
  EnvelopeSimple,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format, isToday, isYesterday } from 'date-fns';
import type { ContactNote } from '../../../types/crm';
import type { SMSMessage, EmailMessage, CallLog } from '../../../types/inbox';
import { NoteCard } from './NoteCard';
import { EmailThreadTimelineCard } from './EmailTimelineCard';
import { CallTimelineCard } from './CallTimelineCard';
import { PhoneNumberSelector } from '../../inbox/PhoneNumberSelector';
import { EmailTemplatePicker } from '../../inbox/EmailTemplatePicker';
import api from '../../../lib/api';
import useWebSocket from '../../../hooks/useWebSocket';

/** Payload of the `sms_received` event (inbox/realtime.py). */
interface InboundSmsPayload {
  sms_id: number;
  contact_id: number | null;
  from_number: string;
  to_number: string;
  body: string;
}

/** Contact notes and SMS paginate at the DRF default of 20 and expose no
    page_size override, so walk the pages instead of rendering only the newest
    20 and silently dropping the rest of the history. */
const MAX_TIMELINE_PAGES = 20;

const fetchAllPages = async <T,>(path: string): Promise<T[]> => {
  const separator = path.includes('?') ? '&' : '?';
  const items: T[] = [];
  for (let page = 1; page <= MAX_TIMELINE_PAGES; page++) {
    const response = await api.get<{ results?: T[]; next?: string | null }>(
      `${path}${separator}page=${page}`
    );
    items.push(...(response.data.results ?? []));
    if (!response.data.next) break;
  }
  return items;
};

/** Every note on a contact, not just the first page. Shared with ContactDetail
    so its Activity count and note modals see the same list the timeline shows.
    The key stays under ['contactNotes', id] so the existing note mutations
    invalidate it. */
export const useAllContactNotes = (contactId?: number) =>
  useQuery({
    queryKey: ['contactNotes', contactId, 'all'],
    queryFn: () =>
      fetchAllPages<ContactNote>(
        `/api/crm/contact-notes/?contact=${contactId}&ordering=-created_at`
      ),
    enabled: !!contactId,
  });

/** Compare two phone numbers on their last 10 digits, ignoring formatting. */
const isSamePhoneNumber = (a?: string, b?: string): boolean => {
  if (!a || !b) return false;
  const digitsA = a.replace(/\D/g, '').slice(-10);
  const digitsB = b.replace(/\D/g, '').slice(-10);
  return digitsA.length === 10 && digitsA === digitsB;
};

interface ContactTimelineProps {
  contactId: number;
  phoneNumber?: string;
  emailAddress?: string;
  leadName: string;
  activeComposer: 'sms' | 'email' | null;
  onAddNote: () => void;
  onEditNote: (note: ContactNote) => void;
  onDeleteNote: (noteId: number) => void;
  onCloseComposer: () => void;
  /** Restrict the feed to one activity type. Defaults to 'all'. */
  typeFilter?: 'all' | 'notes' | 'calls' | 'emails';
  /** Reports how many entries are on screen so the header count can match the
      feed — notes, texts, calls, and email threads, under the active filter. */
  onCountChange?: (count: number) => void;
  /** Rendered after the last item (and inside the empty state) — e.g. a
      "beginning of history" marker. */
  footer?: React.ReactNode;
}

type TimelineItem =
  | { type: 'note'; timestamp: string; data: ContactNote }
  | { type: 'sms'; timestamp: string; data: SMSMessage }
  | { type: 'call'; timestamp: string; data: CallLog }
  // one entry per email THREAD (messages ascending), placed at its latest message
  | { type: 'email'; timestamp: string; data: EmailMessage[] };

const formatDateLabel = (date: Date): string => {
  if (isToday(date)) return 'Today';
  if (isYesterday(date)) return 'Yesterday';
  return format(date, 'MMM d, yyyy');
};

const getDateKey = (dateStr: string): string =>
  format(new Date(dateStr), 'yyyy-MM-dd');

const formatMessageTime = (date: Date): string => format(date, 'h:mm a');

const getStatusIcon = (message: SMSMessage) => {
  if (message.direction === 'inbound') return null;
  switch (message.status) {
    case 'delivered':
      return <DoneAll size={12} />;
    case 'sent':
      return <Check size={12} />;
    case 'failed':
      return <ErrorIcon size={12} color="var(--joy-palette-danger-500)" />;
    default:
      return null;
  }
};

export const ContactTimeline: React.FC<ContactTimelineProps> = ({
  contactId,
  phoneNumber,
  emailAddress,
  leadName,
  activeComposer,
  onAddNote,
  onEditNote,
  onDeleteNote,
  onCloseComposer,
  typeFilter = 'all',
  onCountChange,
  footer,
}) => {
  const navigate = useNavigate();
  const { subscribe } = useWebSocket({ autoConnect: true });
  const { data: notes = [], isLoading: notesLoading } = useAllContactNotes(contactId);
  const [smsMessages, setSmsMessages] = useState<SMSMessage[]>([]);
  const [smsLoading, setSmsLoading] = useState(false);
  const [calls, setCalls] = useState<CallLog[]>([]);
  const [callsLoading, setCallsLoading] = useState(false);
  const [emailMessages, setEmailMessages] = useState<EmailMessage[]>([]);
  const [emailsLoading, setEmailsLoading] = useState(false);
  const [hasEmailAccount, setHasEmailAccount] = useState<boolean | null>(null);
  const [composerText, setComposerText] = useState('');
  const [smsSending, setSmsSending] = useState(false);
  const [smsError, setSmsError] = useState<string | null>(null);
  const [fromNumber, setFromNumber] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailCc, setEmailCc] = useState('');
  const [emailBcc, setEmailBcc] = useState('');
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  useEffect(() => {
    if (!phoneNumber) return;
    fetchSmsMessages();
  }, [phoneNumber]);

  useEffect(() => {
    fetchEmailMessages();
  }, [contactId]);


  useEffect(() => {
    // Does the viewer have a connected Gmail/Outlook account? Decides whether
    // the Email action shows the composer or a connect-your-email prompt.
    api.get<{ accounts: Array<{ is_active: boolean }> }>('/api/inbox/oauth/accounts/')
      .then(response => setHasEmailAccount(
        (response.data.accounts ?? []).some(account => account.is_active)
      ))
      .catch(() => setHasEmailAccount(false));
  }, []);

  /** `silent` refreshes in place — used for live updates, so an arriving
      reply doesn't flash the whole timeline back to its loading state. */
  const fetchSmsMessages = async (silent = false) => {
    if (!phoneNumber) return;
    if (!silent) setSmsLoading(true);
    try {
      setSmsMessages(
        await fetchAllPages<SMSMessage>(
          `/api/inbox/sms/?phone=${encodeURIComponent(phoneNumber)}`
        )
      );
    } catch {
      // silently fail
    } finally {
      if (!silent) setSmsLoading(false);
    }
  };

  /** Calls with this contact, matched on the FK and on the phone number so
      calls placed before the record existed still appear. */
  const fetchCalls = async (silent = false) => {
    if (!silent) setCallsLoading(true);
    try {
      const query = new URLSearchParams({ lead: String(contactId) });
      if (phoneNumber) query.set('phone', phoneNumber);
      setCalls(await fetchAllPages<CallLog>(`/api/inbox/call/?${query.toString()}`));
    } catch {
      // silently fail — the rest of the timeline still renders
    } finally {
      if (!silent) setCallsLoading(false);
    }
  };

  useEffect(() => {
    fetchCalls();
  }, [contactId, phoneNumber]);

  // Live-update the thread when a reply arrives for this contact, so an
  // inbound SMS shows up without a page refresh.
  const fetchSmsRef = useRef(fetchSmsMessages);
  fetchSmsRef.current = fetchSmsMessages;

  useEffect(() => {
    const unsubscribe = subscribe('sms_received', (payload: InboundSmsPayload) => {
      const matchesContact = payload.contact_id != null && payload.contact_id === contactId;
      if (matchesContact || isSamePhoneNumber(payload.from_number, phoneNumber)) {
        fetchSmsRef.current(true);
      }
    });
    return unsubscribe;
  }, [subscribe, contactId, phoneNumber]);

  // Delivery receipts: patch the message in place so the single check turns
  // into the double check without refetching or flashing the timeline.
  useEffect(() => {
    const unsubscribe = subscribe('sms_updated', (payload: SMSMessage) => {
      setSmsMessages(prev =>
        prev.map(message => (message.id === payload.id ? { ...message, ...payload } : message))
      );
    });
    return unsubscribe;
  }, [subscribe]);

  // Calls arrive in stages — the log is created when the call starts, then
  // Twilio reports duration/result, the recording, and the transcription in
  // separate callbacks. Upsert so each stage lands live.
  useEffect(() => {
    const unsubscribe = subscribe('call_updated', (payload: CallLog) => {
      const belongsHere =
        payload.lead === contactId ||
        isSamePhoneNumber(payload.from_number, phoneNumber) ||
        isSamePhoneNumber(payload.to_number, phoneNumber);
      if (!belongsHere) return;

      setCalls(prev => {
        const index = prev.findIndex(call => call.id === payload.id);
        if (index === -1) return [payload, ...prev];
        const next = [...prev];
        next[index] = { ...next[index], ...payload };
        return next;
      });
    });
    return unsubscribe;
  }, [subscribe, contactId, phoneNumber]);

  const fetchEmailMessages = async () => {
    setEmailsLoading(true);
    try {
      const response = await api.get<{ results: EmailMessage[] }>(
        `/api/inbox/email-messages/?contact=${contactId}&page_size=100`
      );
      setEmailMessages(response.data.results ?? []);
    } catch {
      // silently fail — the timeline still shows notes/SMS
    } finally {
      setEmailsLoading(false);
    }
  };

  const handleSend = async () => {
    if (!composerText.trim() || !phoneNumber || smsSending || !fromNumber) return;
    setSmsSending(true);
    setSmsError(null);
    try {
      await postCommunication('/api/inbox/sms/send/', {
        to_number: phoneNumber,
        body: composerText,
        purpose: 'conversation',
        lead_id: contactId,
        ...(fromNumber && { from_number: fromNumber }),
      });
      setComposerText('');
      fetchSmsMessages();
    } catch (err: any) {
      setSmsError(communicationError(err, 'Failed to submit message.'));
    } finally {
      setSmsSending(false);
    }
  };

  const resetEmailComposer = () => {
    setEmailSubject('');
    setEmailBody('');
    setEmailCc('');
    setEmailBcc('');
    setShowCcBcc(false);
    setEmailError(null);
  };

  const splitAddresses = (value: string): string[] =>
    value.split(',').map(a => a.trim()).filter(Boolean);

  const handleSendEmail = async () => {
    if (!emailAddress) return;

    // No connected mailbox: the composer is replaced by the connect prompt, so
    // this only happens if the account check hadn't resolved yet.
    if (!hasEmailAccount) {
      setEmailError('Connect a Gmail or Outlook account in Settings → Profile to send email from ApexOS.');
      return;
    }

    setEmailSending(true);
    setEmailError(null);
    try {
      await api.post('/api/inbox/email/compose/', {
        to_addresses: [emailAddress],
        cc_addresses: splitAddresses(emailCc),
        bcc_addresses: splitAddresses(emailBcc),
        subject: emailSubject,
        body_text: emailBody,
        body_html: `<p>${emailBody.replace(/\n/g, '<br>')}</p>`,
        contact_id: contactId,
      });
      resetEmailComposer();
      onCloseComposer();
      fetchEmailMessages();
    } catch (err: any) {
      setEmailError(err?.response?.data?.error || 'Failed to send email');
    } finally {
      setEmailSending(false);
    }
  };

  const isLoading = notesLoading || smsLoading || emailsLoading || callsLoading;

  // Build merged timeline (newest first)
  const timelineItems: TimelineItem[] = [
    ...notes.map((n): TimelineItem => ({ type: 'note', timestamp: n.created_at, data: n })),
    ...smsMessages.map((s): TimelineItem => ({
      type: 'sms',
      timestamp: s.sent_at || s.created_at,
      data: s,
    })),
    ...calls.map((c): TimelineItem => ({
      type: 'call',
      timestamp: c.started_at || c.created_at,
      data: c,
    })),
    // Group emails into threads, Close-style: one timeline entry per
    // conversation, positioned at its most recent message.
    ...Array.from(
      emailMessages.reduce((byThread, e) => {
        const group = byThread.get(e.thread) || [];
        group.push(e);
        byThread.set(e.thread, group);
        return byThread;
      }, new Map<number, EmailMessage[]>()).values(),
    ).map((group): TimelineItem => {
      const ascending = [...group].sort(
        (a, b) =>
          new Date(a.sent_at || a.created_at).getTime() -
          new Date(b.sent_at || b.created_at).getTime(),
      );
      const latest = ascending[ascending.length - 1];
      return {
        type: 'email',
        timestamp: latest.sent_at || latest.created_at,
        data: ascending,
      };
    }),
  ].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    .filter((item) => {
      if (typeFilter === 'notes') return item.type === 'note';
      // Real call logs plus the manually-logged call notes that predate them.
      if (typeFilter === 'calls') {
        return (
          item.type === 'call' ||
          (item.type === 'note' && (item.data as ContactNote).category === 'call')
        );
      }
      if (typeFilter === 'emails') return item.type === 'email';
      return true;
    });

  useEffect(() => {
    onCountChange?.(timelineItems.length);
  }, [timelineItems.length, onCountChange]);

  // Group by date for separators
  const groups: { dateLabel: string; dateKey: string; items: TimelineItem[] }[] = [];
  for (const item of timelineItems) {
    const dateKey = getDateKey(item.timestamp);
    const existing = groups.find(g => g.dateKey === dateKey);
    if (existing) {
      existing.items.push(item);
    } else {
      groups.push({
        dateKey,
        dateLabel: formatDateLabel(new Date(item.timestamp)),
        items: [item],
      });
    }
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const smsComposer = activeComposer === 'sms' && phoneNumber ? (
    <Sheet variant="outlined" sx={{ p: 1.5, borderRadius: 'md' }}>
      <Stack spacing={1}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <PhoneNumberSelector
            value={fromNumber}
            onChange={setFromNumber}
            smsOnly
            size="sm"
          />
          <IconButton size="sm" variant="soft" color="neutral" onClick={onCloseComposer}>
            <Close size={18} />
          </IconButton>
        </Box>
      <Stack direction="row" spacing={1}>
        <Input
          placeholder="Type a message..."
          value={composerText}
          onChange={e => setComposerText(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          sx={{ flex: 1 }}
          autoFocus
        />
        <IconButton
          size="sm"
          variant="solid"
          color="primary"
          onClick={handleSend}
          disabled={!composerText.trim() || smsSending || !fromNumber}
          loading={smsSending}
        >
          <Send />
        </IconButton>
      </Stack>
      <SMSMessagePreview toNumber={phoneNumber} fromNumber={fromNumber} body={composerText} />
      {smsError && (
        <Typography level="body-xs" sx={{ color: 'danger.500' }}>
          {smsError}
        </Typography>
      )}
      </Stack>
    </Sheet>
  ) : null;

  // No connected mailbox: prompt to connect instead of showing the composer
  // (a mailto: fallback mangles the draft and bounces users out of ApexOS).
  const connectEmailPrompt = (
    <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md', position: 'relative', pr: 5 }}>
      <IconButton size="sm" variant="soft" color="neutral"
        sx={{ position: 'absolute', top: 8, right: 8 }}
        onClick={onCloseComposer}
      >
        <Close size={18} />
      </IconButton>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Sheet
          variant="soft"
          color="primary"
          sx={{ p: 1, borderRadius: 'md', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <EnvelopeSimple size={22} />
        </Sheet>
        <Box sx={{ flex: 1 }}>
          <Typography level="title-sm">Connect your email to send from ApexOS</Typography>
          <Typography level="body-sm" color="neutral" sx={{ mt: 0.5 }}>
            Once Gmail or Outlook is connected, you can email {leadName} right from this page,
            and the conversation shows up here on the timeline.
          </Typography>
          <Button
            size="sm"
            variant="solid"
            color="primary"
            startDecorator={<EnvelopeSimple size={16} />}
            onClick={() => navigate('/settings/profile')}
            sx={{ mt: 1.5 }}
          >
            Connect Email Account
          </Button>
        </Box>
      </Stack>
    </Sheet>
  );

  const emailComposer = activeComposer === 'email' && emailAddress ? (
    hasEmailAccount === false ? connectEmailPrompt : (
    <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md', position: 'relative', pr: 5 }}>
      <IconButton size="sm" variant="soft" color="neutral"
        sx={{ position: 'absolute', top: 8, right: 8 }}
        onClick={onCloseComposer}
      >
        <Close size={18} />
      </IconButton>
      <Stack spacing={1}>
        <EmailTemplatePicker
          contact={{ full_name: leadName, email: emailAddress }}
          onApply={(templateSubject, templateBody) => {
            // Fill the draft without clobbering it: anything already typed
            // stays and the template lands underneath.
            setEmailSubject(prev => prev.trim() || templateSubject);
            setEmailBody(prev => (prev.trim() ? `${prev.trimEnd()}\n\n${templateBody}` : templateBody));
          }}
        />
        <Input
          size="sm"
          value={emailAddress}
          readOnly
          startDecorator={<Typography level="body-xs" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>To:</Typography>}
          sx={{ bgcolor: 'background.level1' }}
        />
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Input
            size="sm"
            placeholder="Subject"
            value={emailSubject}
            onChange={e => setEmailSubject(e.target.value)}
            startDecorator={<Typography level="body-xs" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>Subject:</Typography>}
            autoFocus
            sx={{ flex: 1 }}
          />
          {!showCcBcc && (
            <Button
              size="sm"
              variant="plain"
              color="neutral"
              onClick={() => setShowCcBcc(true)}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Cc/Bcc
            </Button>
          )}
        </Box>
        {showCcBcc && (
          <>
            <Input
              size="sm"
              placeholder="CC recipients (comma-separated)"
              value={emailCc}
              onChange={e => setEmailCc(e.target.value)}
              startDecorator={<Typography level="body-xs" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>Cc:</Typography>}
            />
            <Input
              size="sm"
              placeholder="BCC recipients (comma-separated)"
              value={emailBcc}
              onChange={e => setEmailBcc(e.target.value)}
              startDecorator={<Typography level="body-xs" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>Bcc:</Typography>}
            />
          </>
        )}
        <Textarea
          minRows={3}
          placeholder="Write your email..."
          value={emailBody}
          onChange={e => setEmailBody(e.target.value)}
          sx={{ fontSize: 'sm' }}
        />
        {emailError && (
          <Typography level="body-xs" sx={{ color: 'danger.500' }}>
            {emailError}
          </Typography>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography level="body-xs" color="neutral">
            Sends from your connected email account
          </Typography>
          <Button
            size="sm"
            variant="solid"
            color="primary"
            startDecorator={<Send size={16} />}
            onClick={handleSendEmail}
            disabled={!emailBody.trim() || !emailSubject.trim() || emailSending}
            loading={emailSending}
          >
            Send Email
          </Button>
        </Box>
      </Stack>
    </Sheet>
    )
  ) : null;

  if (timelineItems.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {smsComposer}
        {emailComposer}
        {footer ?? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Comment size={48} color="var(--joy-palette-text-secondary)" style={{ marginBottom: 16 }} />
            <Typography level="body-lg" sx={{ mb: 1 }}>No activity yet</Typography>
            <Typography level="body-sm" color="neutral">
              Add a note, send an SMS, or compose an email to start tracking interactions
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              size="sm"
              startDecorator={<Add />}
              onClick={onAddNote}
              sx={{ mt: 2 }}
            >
              Add Note
            </Button>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {smsComposer}
      {emailComposer}

      {/* Timeline */}
      <Box>
        {groups.map(group => (
          <Box key={group.dateKey}>
            {/* Date Separator */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 2 }}>
              <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
              <Chip size="sm" variant="soft" color="neutral">
                {group.dateLabel}
              </Chip>
              <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
            </Box>

            {/* Items */}
            <Stack spacing={1.5}>
              {group.items.map(item => {
                if (item.type === 'note') {
                  return (
                    <NoteCard
                      key={`note-${item.data.id}`}
                      note={item.data as ContactNote}
                      onEdit={onEditNote}
                      onDelete={onDeleteNote}
                    />
                  );
                }

                if (item.type === 'call') {
                  const call = item.data as CallLog;
                  return <CallTimelineCard key={`call-${call.id}`} call={call} />;
                }

                if (item.type === 'email') {
                  const threadMessages = item.data as EmailMessage[];
                  return (
                    <EmailThreadTimelineCard
                      key={`thread-${threadMessages[0].thread}`}
                      messages={threadMessages}
                      canReply={hasEmailAccount === true}
                      onReplied={fetchEmailMessages}
                    />
                  );
                }

                // SMS bubble
                const sms = item.data as SMSMessage;
                const isInbound = sms.direction === 'inbound';
                const msgTime = new Date(sms.sent_at || sms.created_at);

                return (
                  <Box
                    key={`sms-${sms.id}`}
                    sx={{
                      display: 'flex',
                      justifyContent: isInbound ? 'flex-start' : 'flex-end',
                    }}
                  >
                    <Box sx={{ maxWidth: '70%' }}>
                      {isInbound && (
                        <Typography level="body-xs" color="neutral" sx={{ mb: 0.25, ml: 0.5 }}>
                          {leadName}
                        </Typography>
                      )}
                      <Sheet
                        variant={isInbound ? 'outlined' : 'solid'}
                        color={isInbound ? 'neutral' : 'primary'}
                        sx={{
                          px: 2,
                          py: 1,
                          borderRadius: 'lg',
                          borderBottomLeftRadius: isInbound ? 'xs' : 'lg',
                          borderBottomRightRadius: isInbound ? 'lg' : 'xs',
                        }}
                      >
                        <Typography
                          level="body-sm"
                          sx={{ color: isInbound ? 'text.primary' : 'primary.100' }}
                        >
                          {sms.body}
                        </Typography>
                        {sms.media_urls && sms.media_urls.length > 0 && (
                          <Stack spacing={1} sx={{ mt: 1 }}>
                            {sms.media_urls.map((url, i) => (
                              <Box
                                key={i}
                                component="img"
                                src={url}
                                sx={{ maxWidth: '100%', borderRadius: 'sm' }}
                              />
                            ))}
                          </Stack>
                        )}
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                          <Typography
                            level="body-xs"
                            sx={{ color: isInbound ? 'text.secondary' : 'primary.200' }}
                          >
                            {formatMessageTime(msgTime)}
                          </Typography>
                          {getStatusIcon(sms)}
                        </Stack>
                        {sms.status === 'failed' && sms.error_message && (
                          <Typography level="body-xs" sx={{ color: 'danger.500', mt: 0.5 }}>
                            {sms.error_message}
                          </Typography>
                        )}
                      </Sheet>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        ))}
      </Box>
      {footer}
    </Box>
  );
};

export default ContactTimeline;
